import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    AppBar, Toolbar, Typography, Paper, Box, TextField, Select, MenuItem,
    FormControl, InputLabel, Autocomplete, Alert, IconButton, InputAdornment,
    ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import { ContentCopy, Check, Add, Delete } from '@mui/icons-material';
import MainContentContainer from '../../MainContentContainer/MainContentContainer';
import { fetchAllChallenges } from '../../../actions/challengeActions';
import { fetchAllMarketingInitiatives } from '../../../actions/marketingInitiativeActions';
import { copyToClipboard } from '../../../utils/clipboard';
import SetupGuide from './SetupGuide';

const PREMIUM_DURATION_OPTIONS = [
    { value: '', label: 'None' },
    { value: '1wk', label: '1 Week' },
    { value: '2wk', label: '2 Weeks' },
    { value: '1mo', label: '1 Month' },
    { value: '3mo', label: '3 Months' },
    { value: '6mo', label: '6 Months' },
    { value: '1yr', label: '1 Year' },
    { value: 'lifetime', label: 'Lifetime' },
];

export default function ClickFunnelsWebhookPage() {
    const dispatch = useDispatch();

    const challengesMap = useSelector(state => state.challenges.challenges);
    const initiatives = useSelector(state => state.initiatives.initiatives);

    useEffect(() => {
        dispatch(fetchAllChallenges());
        dispatch(fetchAllMarketingInitiatives());
    }, [dispatch]);

    const challengeOptions = useMemo(() => {
        if (!challengesMap) return [];
        return Object.values(challengesMap).map(c => ({
            id: c.challenge_id,
            label: `${c.challenge_title} (ID: ${c.challenge_id})`,
        }));
    }, [challengesMap]);

    const initiativeOptions = useMemo(() => {
        if (!initiatives || !Array.isArray(initiatives)) return [];
        return initiatives
            .filter(i => i.is_active === 1 || i.is_active === '1')
            .map(i => ({
                code: i.code,
                label: `${i.name} (${i.code})`,
            }));
    }, [initiatives]);

    const [environment, setEnvironment] = useState('production');
    const [secret, setSecret] = useState('');
    const [selectedChallenge, setSelectedChallenge] = useState(null);
    const [premiumDuration, setPremiumDuration] = useState('');
    const [selectedInitiative, setSelectedInitiative] = useState(null);
    const [marketingSource, setMarketingSource] = useState('');
    const [brazeAttrs, setBrazeAttrs] = useState([]);
    const [klaviyoListId, setKlaviyoListId] = useState('');
    const [copied, setCopied] = useState(false);

    const webhookUrl = useMemo(() => {
        const base = environment === 'production'
            ? 'https://api.thetappingsolution.com'
            : 'https://staging.api.thetappingsolution.com';

        const params = new URLSearchParams();
        if (secret) params.set('secret', secret);
        if (selectedChallenge) params.set('challenge_id', selectedChallenge.id);
        if (premiumDuration) params.set('premium_duration', premiumDuration);
        if (selectedInitiative) params.set('initiative_code', selectedInitiative.code);
        if (marketingSource) params.set('marketing_source', marketingSource);
        brazeAttrs.forEach((attr, i) => {
            if (attr.name && attr.value) {
                params.set(`braze_attr[${i}]`, `${attr.name}|${attr.value}`);
            }
        });
        if (klaviyoListId) params.set('klaviyo_list_id', klaviyoListId);

        const queryString = params.toString();
        const secretPrefix = secret ? '' : 'secret=<WEBHOOK_SECRET>';
        const separator = secretPrefix && queryString ? '&' : '';
        return `${base}/api/webhook/clickfunnels/create-profile?${secretPrefix}${separator}${queryString}`;
    }, [environment, secret, selectedChallenge, premiumDuration, selectedInitiative, marketingSource, brazeAttrs, klaviyoListId]);

    const handleCopyUrl = () => {
        copyToClipboard(webhookUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <MainContentContainer>
            <AppBar position="sticky">
                <Toolbar>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        ClickFunnels Webhook Integration
                    </Typography>
                </Toolbar>
            </AppBar>

            {/* URL Builder */}
            <Paper sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" gutterBottom>Webhook URL Builder</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Select the parameters for your funnel and copy the generated webhook URL into ClickFunnels.
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {/* Environment Toggle */}
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Environment</Typography>
                        <ToggleButtonGroup
                            value={environment}
                            exclusive
                            onChange={(_, val) => val && setEnvironment(val)}
                            size="small"
                        >
                            <ToggleButton value="production">Production</ToggleButton>
                            <ToggleButton value="staging">Staging</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {/* Secret */}
                    <TextField
                        label="Webhook Secret"
                        value={secret}
                        onChange={(e) => setSecret(e.target.value)}
                        placeholder="Paste CLICKFUNNELS_WEBHOOK_SECRET here"
                        helperText="From the environment's CLICKFUNNELS_WEBHOOK_SECRET variable. Leave empty to use a placeholder."
                    />

                    {/* Challenge */}
                    <Autocomplete
                        options={challengeOptions}
                        value={selectedChallenge}
                        onChange={(_, val) => setSelectedChallenge(val)}
                        renderInput={(params) => <TextField {...params} label="Challenge" placeholder="Search challenges..." />}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                    />

                    {/* Premium Duration */}
                    <FormControl fullWidth>
                        <InputLabel>Premium Duration</InputLabel>
                        <Select
                            value={premiumDuration}
                            label="Premium Duration"
                            onChange={(e) => setPremiumDuration(e.target.value)}
                        >
                            {PREMIUM_DURATION_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Initiative */}
                    <Autocomplete
                        options={initiativeOptions}
                        value={selectedInitiative}
                        onChange={(_, val) => setSelectedInitiative(val)}
                        renderInput={(params) => <TextField {...params} label="Initiative" placeholder="Search initiatives..." />}
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                    />

                    {/* Marketing Source */}
                    <TextField
                        label="Marketing Source"
                        value={marketingSource}
                        onChange={(e) => setMarketingSource(e.target.value)}
                        placeholder="e.g. cf_landing_page"
                        helperText={'Sets Amplitude "Account Creation Source" for new users (defaults to "ClickFunnels"). Also used as "Marketing Source" in initiative activation events.'}
                    />

                    {/* Braze Attributes */}
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2">Braze Custom Attributes</Typography>
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() => setBrazeAttrs([...brazeAttrs, { name: '', value: '' }])}
                                sx={{ ml: 1 }}
                            >
                                <Add fontSize="small" />
                            </IconButton>
                        </Box>
                        {brazeAttrs.map((attr, i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                <TextField
                                    size="small"
                                    label="Attribute Name"
                                    value={attr.name}
                                    onChange={(e) => {
                                        const updated = [...brazeAttrs];
                                        updated[i] = { ...updated[i], name: e.target.value };
                                        setBrazeAttrs(updated);
                                    }}
                                    placeholder="e.g. Campaign Source"
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    size="small"
                                    label="Attribute Value"
                                    value={attr.value}
                                    onChange={(e) => {
                                        const updated = [...brazeAttrs];
                                        updated[i] = { ...updated[i], value: e.target.value };
                                        setBrazeAttrs(updated);
                                    }}
                                    placeholder="e.g. summer_2025"
                                    sx={{ flex: 1 }}
                                />
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setBrazeAttrs(brazeAttrs.filter((_, j) => j !== i))}
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                        {brazeAttrs.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                Click + to add Braze custom attributes (name|value pairs)
                            </Typography>
                        )}
                    </Box>

                    {/* Klaviyo List ID */}
                    <TextField
                        label="Klaviyo List ID"
                        value={klaviyoListId}
                        onChange={(e) => setKlaviyoListId(e.target.value)}
                        placeholder="e.g. XyZ123"
                    />

                    {/* Generated URL */}
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Generated Webhook URL</Typography>
                        <TextField
                            fullWidth
                            multiline
                            value={webhookUrl}
                            InputProps={{
                                readOnly: true,
                                sx: { fontFamily: 'monospace', fontSize: '0.8rem' },
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleCopyUrl} edge="end">
                                            {copied ? <Check color="success" /> : <ContentCopy />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
{!secret && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            Paste the webhook secret above to generate a ready-to-use URL. Without it, the URL contains a <code>&lt;WEBHOOK_SECRET&gt;</code> placeholder.
                        </Alert>
                        )}
                    </Box>
                </Box>
            </Paper>

            <SetupGuide />
        </MainContentContainer>
    );
}
